/**
 * Required Fields Validation Hook
 *
 * React hook that validates required fields for the currently selected
 * coverage and returns the validation result for use in the
 * CoverageCompletionButton disabled state.
 */

import { useMemo } from "react";

import { LOC_MAP, type LocCoverageType } from "../locMap";

import { EMPTY_VALIDATION_RESULT } from "./types";
import { getRequiredFieldKeys, validateRequiredFields } from "./validation";

import type { RequiredFieldsValidationResult } from "./types";
import type { RfpCoverage } from "../../api/types";

// Per-plan field that the group-level Current Policy supersedes when the
// Current Policy dropdown is enabled (ADR 10.22), so it must not block
// completion once the field is hidden from the grid.
const CURRENT_CARRIER_NAME_KEY = "currentCarrierName";

// Voluntary Life's three fixed child benefit-description rows (Type + nested
// Amount), required today. When btx-ch-benefits-field is on they're hidden in
// favor of the flexible childBenefitAmountsByAge row (CD-1976 parity with
// Present - see hideProposalOnlyRows), so they must not block completion, and
// the flexible row takes over as the required one. childLimitingAge is
// omitted: it was never itself required.
const CHILD_FIXED_AGE_BAND_KEYS: ReadonlySet<string> = new Set([
  "benefitDescriptionChildDependentAgesBirthTo14DaysType",
  "benefitDescriptionChildDependentAgesBirthTo14DaysAmount",
  "benefitDescriptionChildDependentAges15DaysTo6MonthsType",
  "benefitDescriptionChildDependentAges15DaysTo6MonthsAmount",
  "benefitDescriptionChildDependentAges6MonthsToLimitingAgeType",
  "benefitDescriptionChildDependentAges6MonthsToLimitingAgeAmount",
]);
const CHILD_AGE_BANDS_KEY = "childBenefitAmountsByAge";

/** Internal - exported for testing; use the hook in application code. */
export const resolveRequiredKeys = (
  keys: ReadonlySet<string>,
  currentCarrierPolicyEnabled: boolean,
  childAgeBandsFieldEnabled: boolean,
): ReadonlySet<string> => {
  let resolved = keys;

  if (currentCarrierPolicyEnabled) {
    const filtered = new Set(resolved);
    filtered.delete(CURRENT_CARRIER_NAME_KEY);
    resolved = filtered;
  }

  if (childAgeBandsFieldEnabled) {
    // Only Voluntary Life's required set contains these keys - other LOCs'
    // sets pass through untouched, so this never adds childBenefitAmountsByAge
    // as a required key for a coverage that has no such field.
    const hasFixedChildAgeBands = [...CHILD_FIXED_AGE_BAND_KEYS].some((key) =>
      resolved.has(key),
    );

    if (hasFixedChildAgeBands) {
      const filtered = new Set(resolved);
      CHILD_FIXED_AGE_BAND_KEYS.forEach((key) => filtered.delete(key));
      filtered.add(CHILD_AGE_BANDS_KEY);
      resolved = filtered;
    }
  }

  return resolved;
};

/**
 * Validates whether all required fields have values for a coverage.
 *
 * Looks up the LOC config for the coverage type, derives the set of
 * required fields from the LOC definitions (via `isRequired: true`),
 * transforms benefit data to grid format, and checks each required field
 * for a valid value. Returns a validation result with missing field details.
 *
 * @param coverage - The currently selected coverage (or undefined if none)
 * @returns Validation result with isValid, missingFields, and counts
 *
 * @example
 * ```tsx
 * const validation = useRequiredFieldsValidation(selectedCoverage);
 * // validation.isValid → false if any required fields are missing
 * // validation.missingFields → list of { fieldKey, label, planIdentifier, planName }
 * ```
 */
export function useRequiredFieldsValidation(
  coverage: RfpCoverage | undefined,
  currentCarrierPolicyEnabled: boolean,
  childAgeBandsFieldEnabled = false,
): RequiredFieldsValidationResult {
  return useMemo(() => {
    if (!coverage) {
      return EMPTY_VALIDATION_RESULT;
    }

    const locConfig = LOC_MAP[coverage.type as LocCoverageType];
    if (!locConfig) {
      return EMPTY_VALIDATION_RESULT;
    }

    // Derive required field keys from the LOC definitions (single source of
    // truth), then drop the per-plan Current Carrier when the Current Policy
    // dropdown supersedes it, and swap Voluntary Life's fixed child
    // benefit-description keys for the flexible one when that flag is on.
    const requiredFieldKeys = resolveRequiredKeys(
      getRequiredFieldKeys(locConfig.definitions),
      currentCarrierPolicyEnabled,
      childAgeBandsFieldEnabled,
    );
    if (requiredFieldKeys.size === 0) {
      return EMPTY_VALIDATION_RESULT;
    }

    return validateRequiredFields(
      coverage.benefitPlans,
      requiredFieldKeys,
      locConfig.definitions,
      locConfig.transformFromApi as (
        benefits: unknown,
      ) => Record<string, unknown>,
    );
  }, [coverage, currentCarrierPolicyEnabled, childAgeBandsFieldEnabled]);
}
